import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  OutlinedInput,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  InputLabel,
  FormControl, Snackbar
} from '@mui/material';
import {
  createCompetitionPlace,
  createNewRewards,
  CreateRewardsConfig, deleteCompetitionPlace, deleteReward,
  getCompetitionPlaces,
  getShopItems, updateCompetitionPlace, updateReward
} from '../../../api/settings';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import MuiAlert, { AlertColor } from '@mui/material/Alert';

type RewardsItemType = {
  id?: string,
  item?: any,
  itemId?: string | null,
  kind: string,
  placeId: string,
  type: 'QUANTITY' | 'PERCENTS',
  value: number
}

type CompetitionItemType = {
  competitionId?: string,
  firstPlace: number,
  id?: string,
  lastPlace: number,
  rewards: RewardsItemType[],
  type: 'RANDOM_BOOSTER' | 'RANDOM_STICKER' | 'COINS' | 'ENERGY' | 'RANDOM_PRIZE_BOX' | 'SHOP_ITEM'
}

const competitionTypes = ['EQUAL', 'BETWEEN', 'GREATER_THAN_EQUAL'];
const rewardsKind = ['RANDOM_BOOSTER', 'RANDOM_STICKER', 'COINS', 'RANDOM_PRIZE_BOX', 'SHOP_ITEM'];
const rewardsType = ['QUANTITY', 'PERCENTS'];

const ManageCompetitionRewardsDialog = ({ isOpen, close, competitionId }: { isOpen: boolean; close(): void; competitionId: string }) => {
  const [competitionPlaces, setCompetitionPlaces] = useState<CompetitionItemType[] | []>([]);
  const [newCompetitionPlace, setNewCompetitionPlace] = useState({
    isOpen: false,
    competitionId,
    type: competitionTypes[0] as 'EQUAL' | 'BETWEEN' | 'GREATER_THAN_EQUAL',
    firstPlace: 1,
    lastPlace: 0
  });
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [isOpenNewRewards, setIsOpenNewRewards] = useState(false);
  const [newCompetitionRewards, setNewCompetitionRewards] = useState<CreateRewardsConfig>({
    kind: rewardsKind[0] as 'RANDOM_BOOSTER' | 'RANDOM_STICKER' | 'COINS' | 'ENERGY' | 'RANDOM_PRIZE_BOX' | 'SHOP_ITEM',
    type: rewardsType[0] as 'QUANTITY' | 'PERCENTS',
    value: 0,
    itemId: undefined,
    placeId: selectedPlace?.id
  });
  const [shopItemsList, setShopItemsList] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState<{isOpen: boolean, alertType: AlertColor, message: string}>({
    isOpen: false,
    alertType: 'success',
    message: ''
  });
  
  useEffect(() => {
    (async () => {
      if (!!competitionId && isOpen) {
        setCompetitionPlaces(await getCompetitionPlaces(competitionId));
        setShopItemsList(await getShopItems());
      }
    })();
  }, [competitionId, isOpen]);
  
  useEffect(() => {
    if (!!selectedPlace) {
      setNewCompetitionRewards({...newCompetitionRewards, placeId: selectedPlace.id});
    }
  }, [selectedPlace]);
  
  const handleCreateNewPlace = async () => {
    if (competitionId) {
      try {
        await createCompetitionPlace({
          competitionId: competitionId ,
          firstPlace: newCompetitionPlace.firstPlace,
          lastPlace: newCompetitionPlace.lastPlace,
          type: newCompetitionPlace.type
        });
        setCompetitionPlaces(await getCompetitionPlaces(competitionId));
        setNewCompetitionPlace({
          isOpen: false,
          competitionId,
          type: competitionTypes[0] as 'EQUAL' | 'BETWEEN' | 'GREATER_THAN_EQUAL',
          firstPlace: 1,
          lastPlace: 0
        });
        setOpenSnackbar({
          isOpen: true,
          alertType: 'success',
          message: 'New competition place has been successfully created!'
        });
      } catch (e) {
        setOpenSnackbar({
          isOpen: true,
          alertType: 'error',
          message: 'New competition place was not created, please check the correctness of the data and try again!'
        });
      }
    }
  };
  
  const closeNewRewards = () => {
    setIsOpenNewRewards(false);
    setSelectedPlace(null);
    setNewCompetitionRewards({
      kind: rewardsKind[0] as 'RANDOM_BOOSTER' | 'RANDOM_STICKER' | 'COINS' | 'ENERGY' | 'RANDOM_PRIZE_BOX' | 'SHOP_ITEM',
      type: rewardsType[0] as 'QUANTITY' | 'PERCENTS',
      value: 0,
      itemId: '',
      placeId: selectedPlace?.id
    })
  };
  
  const handleCreateNewRewards = async () => {
    try {
      await createNewRewards(newCompetitionRewards);
      setCompetitionPlaces(await getCompetitionPlaces(competitionId));
      closeNewRewards();
      setOpenSnackbar({
        isOpen: true,
        alertType: 'success',
        message: 'New rewards has been successfully created!'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        alertType: 'error',
        message: 'New rewards was not created, please check the correctness of the data and try again!'
      });
    }
  };
  
  const handleDeletePlace = async (place: CompetitionItemType) => {
    try {
      await deleteCompetitionPlace(place.id as string);
      setCompetitionPlaces(await getCompetitionPlaces(competitionId));
      setOpenSnackbar({
        isOpen: true,
        alertType: 'success',
        message: 'Competition place has been successfully deleted!'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        alertType: 'error',
        message: 'Competition place was not deleted, try again!'
      });
    }
  };
  
  const handleDeleteRewards = async (reward: RewardsItemType) => {
    try {
      await deleteReward(reward.id as string);
      setCompetitionPlaces(await getCompetitionPlaces(competitionId));
      setOpenSnackbar({
        isOpen: true,
        alertType: 'success',
        message: 'Reward has been successfully deleted!'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        alertType: 'error',
        message: 'Reward was not deleted, try again!'
      });
    }
  };
  
  const changePlace = async (updatedCompetition: any) => {
    try {
      const config = {
        id: updatedCompetition.id ?? '',
        type: updatedCompetition.type ?? '',
        firstPlace: updatedCompetition.firstPlace,
        lastPlace: updatedCompetition.lastPlace
      };
      
      await updateCompetitionPlace(config);
      setCompetitionPlaces(await getCompetitionPlaces(competitionId));
      setOpenSnackbar({
        isOpen: true,
        alertType: 'success',
        message: 'Competition has been successfully updated!'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        alertType: 'error',
        message: 'Competition was not updated, please check the correctness of the data and try again!'
      });
    }
  };
  
  const changeReward = async (updatedReward: any) => {
    try {
      const config = {
        kind: updatedReward.kind,
        type: updatedReward.type,
        value: updatedReward.value,
        itemId: updatedReward.itemId,
        id: updatedReward.id
      };
      
      await updateReward(config);
      setCompetitionPlaces(await getCompetitionPlaces(competitionId));
      setOpenSnackbar({
        isOpen: true,
        alertType: 'success',
        message: 'Reward has been successfully updated!'
      });
    } catch (e) {
      setOpenSnackbar({
        isOpen: true,
        alertType: 'error',
        message: 'Reward was not updated, please check the correctness of the data and try again!'
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>Update competition places and rewards</DialogTitle>
      <DialogContent>
        {competitionPlaces?.length > 0 && competitionPlaces.map((place: any, index: number) => {
          return (
            <div className="flex items-center w-full mb-5" key={place?.id}>
              <div className="p-5 border rounded-md border-[#a1a1a1] w-full">
                <div className="flex items-center">
                  <TextField
                    variant="outlined"
                    defaultValue={place.firstPlace.toString()}
                    className="w-full"
                    label="First place"
                    type="number"
                    onBlur={(event) =>
                      changePlace({...place, firstPlace: Number(event.target.value)})
                    }
                  />
                  <Select
                    labelId="characteristic-type-label"
                    id="characteristic-type-select"
                    value={place.type}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected?.length === 0) return <em className="opacity-70">Type</em>;
                      return selected;
                    }}
                    onChange={(event: any) => changePlace({...place, type: event.target.value})}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                    className="w-[250px] ml-5"
                  >
                    {competitionTypes.map((type, index) => (
                      <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
                    ))}
                  </Select>
                  <TextField
                    variant="outlined"
                    defaultValue={place?.lastPlace?.toString()}
                    className="w-full !ml-5"
                    label="Last place"
                    type="number"
                    disabled={place.type !== 'BETWEEN'}
                    onBlur={(event) =>
                      changePlace({...place, lastPlace: Number(event.target.value)})
                    }
                  />
                </div>
                <div className="mt-3">
                  <em className="block opacity-70 mb-2">Rewards:</em>
                  {place.rewards.map((item: any, index: number) => {
                    return (
                      <div
                        className="mb-2 p-3 w-full border-b border-[#a1a1a1] flex items-center"
                        key={item?.id}
                      >
                        <FormControl className="w-full">
                          <InputLabel id="reward-kind-select-label">Kind</InputLabel>
                          <Select
                            labelId="reward-kind-select-label"
                            value={item.kind}
                            renderValue={(selected) => {
                              if (selected?.length === 0) return <em className="opacity-70">Kind</em>;
                              return selected;
                            }}
                            onChange={(event: any) => {
                              if (event.target.value !== 'COINS') {
                                changeReward({...item, type: 'QUANTITY', kind: event.target.value});
                                return;
                              }
                              changeReward({...item, kind: event.target.value})
                            }}
                            displayEmpty
                            //changeReward({...item, kind: event.target.value})
                            label="Kind"
                          >
                            {rewardsKind.map((type, index) => (
                              <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl className="w-full !ml-5">
                          <InputLabel id="reward-kind-select-label">Type</InputLabel>
                          <Select
                            labelId="reward-kind-select-label"
                            value={item.type}
                            renderValue={(selected) => {
                              if (selected?.length === 0) return <em className="opacity-70">Rewards type</em>;
                              return selected;
                            }}
                            onChange={(event: any) => changeReward({...item, type: event.target.value})}
                            displayEmpty
                            label="Type"
                            disabled={item.kind !== 'COINS'}
                          >
                            {rewardsType.map((type, index) => (
                              <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          variant="outlined"
                          defaultValue={item.value}
                          className="w-full !ml-5"
                          label="Value"
                          type="number"
                          onBlur={(event) =>
                            changeReward({...item, value: Number(event.target.value)})
                          }
                        />
                        {item.kind === 'SHOP_ITEM' && (
                          <FormControl className="w-full !ml-5">
                            <InputLabel id="reward-kind-select-label">Shop items</InputLabel>
                            <Select
                              labelId="reward-kind-select-label"
                              value={item.itemId}
                              onChange={(event: any) => setNewCompetitionRewards({...newCompetitionRewards, itemId: event.target.value})}
                              displayEmpty
                              label="Shop items"
                            >
                              {shopItemsList.map((item: any, index: number) => (
                                <MenuItem value={item.id} key={index.toString()}>{item.name}({item.type})</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        <DeleteForeverTwoToneIcon
                          color="error"
                          className="!ml-3 cursor-pointer"
                          onClick={() => handleDeleteRewards(item)}
                        />
                      </div>
                    )
                  })}
                </div>
                <Button
                  variant="contained"
                  className="!mt-5"
                  onClick={() => {
                    setIsOpenNewRewards(true);
                    setSelectedPlace(place);
                  }}
                >
                  Add rewards
                </Button>
              </div>
              <DeleteForeverTwoToneIcon
                color="error"
                className="!ml-3 cursor-pointer"
                onClick={() => handleDeletePlace(place)}
              />
            </div>
          )
        })}
        <div>
          {newCompetitionPlace.isOpen && (
            <div className="border-b border-[#a1a1a1] pb-3">
              <p className="opacity-70 italic border-b border-[#a1a1a1] mb-3">New competition place</p>
              <div className="flex items-center justify-between">
                <TextField
                  value={newCompetitionPlace.firstPlace.toString()}
                  variant="filled"
                  label="First place"
                  type="number"
                  onChange={(event) =>
                    setNewCompetitionPlace({...newCompetitionPlace, firstPlace: Number(event.target.value) })
                  }
                />
                <Select
                  value={newCompetitionPlace.type}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected?.length === 0) return <em className="opacity-70">Type</em>;
                    return selected;
                  }}
                  onChange={(event: any) => setNewCompetitionPlace({...newCompetitionPlace, type: event.target.value})}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  className="w-[250px] !ml-10"
                >
                  {competitionTypes.map((type, index) => (
                    <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
                  ))}
                </Select>
                <TextField
                  value={newCompetitionPlace.lastPlace.toString()}
                  variant="filled"
                  className="!ml-10"
                  label="Last place"
                  disabled={newCompetitionPlace.type !== 'BETWEEN'}
                  type="number"
                  onChange={(event) =>
                    setNewCompetitionPlace({...newCompetitionPlace, lastPlace: Number(event.target.value) })
                  }
                />
                <div className="flex items-center justify-between">
                  <Tooltip title="Create new competition place" placement="top">
                    <SaveTwoToneIcon
                      color="primary"
                      className="cursor-pointer !ml-5"
                      onClick={handleCreateNewPlace}
                    />
                  </Tooltip>
                  <Tooltip title="Cancel creation new competition place" placement="top">
                    <HighlightOffTwoToneIcon
                      color="error"
                      className="!ml-3 cursor-pointer"
                      onClick={() => setNewCompetitionPlace({
                        isOpen: false,
                        competitionId,
                        type: competitionTypes[0] as 'EQUAL' | 'BETWEEN' | 'GREATER_THAN_EQUAL',
                        firstPlace: 1,
                        lastPlace: 0
                      })}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
          <Button
            onClick={() => setNewCompetitionPlace({...newCompetitionPlace, isOpen: true})}
            variant="contained"
            disabled={newCompetitionPlace.isOpen}
            className="!mt-5"
          >
            Add new place
          </Button>
        </div>
      </DialogContent>
      <Dialog open={isOpenNewRewards} onClose={closeNewRewards} fullWidth maxWidth="sm">
        <DialogTitle>Add new rewards</DialogTitle>
        <div className="pb-5 px-5 flex items-center justify-between w-full">
          <FormControl className="w-[250px]">
            <InputLabel id="reward-kind-select-label">Rewards kind</InputLabel>
            <Select
              labelId="reward-kind-select-label"
              value={newCompetitionRewards.kind}
              renderValue={(selected) => {
                if (selected?.length === 0) return <em className="opacity-70">Rewards kind</em>;
                return selected;
              }}
              onChange={(event: any) => {
                setNewCompetitionRewards({...newCompetitionRewards, kind: event.target.value});
                if (event.target.value !== 'COINS') {
                  setNewCompetitionRewards({...newCompetitionRewards, type: 'QUANTITY', kind: event.target.value});
                }
              }}
              displayEmpty
              label="Rewards kind"
            >
              {rewardsKind.map((type, index) => (
                <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl className="w-[250px]">
            <InputLabel id="reward-kind-select-label">Rewards type</InputLabel>
            <Select
              labelId="reward-kind-select-label"
              value={newCompetitionRewards.type}
              renderValue={(selected) => {
                if (selected?.length === 0) return <em className="opacity-70">Rewards type</em>;
                return selected;
              }}
              onChange={(event: any) => setNewCompetitionRewards({...newCompetitionRewards, type: event.target.value})}
              displayEmpty
              label="Rewards type"
              disabled={newCompetitionRewards.kind !== 'COINS'}
            >
              {rewardsType.map((type, index) => (
                <MenuItem value={type} key={index.toString()}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="pb-5 px-5 flex items-center justify-between">
          <TextField
            value={newCompetitionRewards.value.toString()}
            variant="filled"
            label="Number value"
            type="number"
            className="w-[250px]"
            onChange={(event) =>
              setNewCompetitionRewards({...newCompetitionRewards, value: Number(event.target.value) })
            }
          />
          {newCompetitionRewards.kind === 'SHOP_ITEM' && (
            <FormControl className="w-[250px]">
              <InputLabel id="reward-kind-select-label">Shop items</InputLabel>
              <Select
                labelId="reward-kind-select-label"
                value={newCompetitionRewards.itemId}
                onChange={(event: any) => setNewCompetitionRewards({...newCompetitionRewards, itemId: event.target.value})}
                displayEmpty
                label="Shop items"
              >
                {shopItemsList.map((item: any, index: number) => (
                  <MenuItem value={item.id} key={index.toString()}>{item.name}({item.type})</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>
        <div className="flex items-center justify-between px-10 pb-5">
          <Button
            variant="contained"
            color="error"
            onClick={closeNewRewards}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewRewards}
          >
            Add new rewards
          </Button>
        </div>
      </Dialog>
      <Snackbar
        open={openSnackbar.isOpen}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
      >
        <MuiAlert
          onClose={() => setOpenSnackbar({...openSnackbar, isOpen: false})}
          severity={openSnackbar.alertType}
          sx={{ width: '100%' }}
        >
          {openSnackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  )
};

export default ManageCompetitionRewardsDialog;